import React from 'react';
import RentOutHeroModal from './RentOutHeroModal';
import StopRentOutTransactionModal from './StopRentOutTransactionModal';
import TransactionSuccessModal from './TransactionSuccessModal';
import TransactionFailureModal from './TransactionFailureModal';
import MaintenanceModal from './MaintenanceModal';
import { AppError } from 'src/redux/types';
import WalletConnectConfirmProvider from 'src/Components/WalletConnectConfirmProvider';
import { TestableProvider } from '@wolffun/testable-id';
var RentOutModalName;

(function (RentOutModalName) {
  RentOutModalName["RENT_OUT_TRANSACTION"] = "rent_out_transaction";
  RentOutModalName["TRANSACTION_SUCCESS"] = "transaction_success";
  RentOutModalName["TRANSACTION_FAILURE"] = "transaction_failure";
  RentOutModalName["STOP_RENT_OUT_TRANSACTION"] = "stop_rent_out_transaction";
  RentOutModalName["MAINTENANCE"] = "maintenance";
})(RentOutModalName || (RentOutModalName = {}));

const noop = () => {};

const RentOutContext = /*#__PURE__*/React.createContext({
  rentOut: () => noop,
  stopRentOut: () => noop,
  openModal: noop,
  closeModal: noop,
  showMaintenance: noop
});

const RentOutProvider = ({
  children
}) => {
  const [hero, setHero] = React.useState(null);
  const [successData, setSuccessData] = React.useState(null);
  const [modal, setModal] = React.useState(null);
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const rentOut = React.useCallback(hero => {
    setHero(hero);
    setModal(RentOutModalName.RENT_OUT_TRANSACTION);
  }, []);
  const stopRentOut = React.useCallback(hero => {
    setHero(hero);
    setModal(RentOutModalName.STOP_RENT_OUT_TRANSACTION);
  }, []);
  const openModal = React.useCallback(modal => {
    setModal(modal);
  }, []);
  const closeModal = React.useCallback(() => {
    setHero(null);
    setModal(null);
  }, []);
  const showMaintenance = React.useCallback(() => {
    setModal(RentOutModalName.MAINTENANCE);
  }, []);
  const context = React.useMemo(() => {
    return {
      hero: hero || undefined,
      modal: modal || undefined,
      rentOut,
      openModal,
      closeModal,
      stopRentOut,
      showMaintenance
    };
  }, [closeModal, hero, modal, openModal, rentOut, showMaintenance, stopRentOut]);

  const renderModal = () => {
    switch (modal) {
      case RentOutModalName.RENT_OUT_TRANSACTION:
        return hero ? /*#__PURE__*/React.createElement(RentOutHeroModal, {
          hero: hero,
          onCompleted: data => {
            setSuccessData(data);
            openModal(RentOutModalName.TRANSACTION_SUCCESS);
          },
          onClose: closeModal
        }) : null;

      case RentOutModalName.STOP_RENT_OUT_TRANSACTION:
        return hero ? /*#__PURE__*/React.createElement(StopRentOutTransactionModal, {
          hero: hero,
          onCompleted: () => {
            openModal(RentOutModalName.TRANSACTION_SUCCESS);
            location.reload();
          },
          onFailure: error => {
            openModal(RentOutModalName.TRANSACTION_FAILURE);
            setError(error);
          },
          onClose: closeModal
        }) : null;

      case RentOutModalName.TRANSACTION_SUCCESS:
        return successData && /*#__PURE__*/React.createElement(TransactionSuccessModal, {
          data: successData,
          onClose: () => {
            closeModal();
            location.reload();
          }
        });

      case RentOutModalName.TRANSACTION_FAILURE:
        return /*#__PURE__*/React.createElement(TransactionFailureModal, {
          error: error,
          onClose: closeModal
        });

      case RentOutModalName.MAINTENANCE:
        return /*#__PURE__*/React.createElement(MaintenanceModal, {
          onClose: () => {
            closeModal();
            location.reload();
          }
        });

      default:
        return null;
    }
  };

  return /*#__PURE__*/React.createElement(WalletConnectConfirmProvider, null, /*#__PURE__*/React.createElement(RentOutContext.Provider, {
    value: context
  }, children, /*#__PURE__*/React.createElement(TestableProvider, {
    testId: "rent-out"
  }, renderModal())));
};

export const useRentOut = () => {
  const {
    rentOut,
    stopRentOut,
    showMaintenance
  } = React.useContext(RentOutContext);
  return {
    rentOut,
    stopRentOut,
    showMaintenance
  };
};
export default RentOutProvider;